import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationCircle,
  faBookOpen,
  faUserGraduate,
  faLayerGroup,
  faClipboardList,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';

interface InstructorCourse {
  learnershipId: string;
  title: string;
  saqaId: string;
  nqfLevel: number | null;
  moduleCount: number;
  enrolledStudentCount: number;
  pendingSubmissionsToGrade: number;
}

export default function InstructorCoursesPage() {
  const [courses, setCourses] = useState<InstructorCourse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { accessToken } = useAuth();

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch('/api/instructor/courses', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) throw new Error('Failed to load courses');
        setCourses(await response.json());
      } catch (err) {
        console.error(err);
        setError('Failed to load your courses. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCourses();
  }, [accessToken]);

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">My Courses</h1>
        <p className="text-sm text-brand-900/60 mt-1">Courses and learnerships assigned to you</p>
      </div>

      <div className="p-8">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && courses.length === 0 && (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faBookOpen} className="h-12 w-12 text-brand-300 mb-4" />
            <h3 className="text-lg font-bold text-brand-950">No courses assigned yet</h3>
            <p className="text-sm text-brand-900/60 mt-2">
              Ask an administrator to assign you to a course from the admin panel.
            </p>
          </div>
        )}

        {!isLoading && !error && courses.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {courses.map((course) => (
              <Link
                key={course.learnershipId}
                to={`/instructor/courses/${course.learnershipId}`}
                className="card-surface p-6 block hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-brand-gradient flex items-center justify-center text-white flex-shrink-0">
                      <FontAwesomeIcon icon={faBookOpen} className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-brand-950">{course.title}</h3>
                      <p className="text-xs text-brand-900/50 mt-1">
                        SAQA {course.saqaId || '—'}
                        {course.nqfLevel ? ` · NQF ${course.nqfLevel}` : ''}
                      </p>
                    </div>
                  </div>
                  {course.pendingSubmissionsToGrade > 0 && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-yellow-100 text-yellow-800">
                      {course.pendingSubmissionsToGrade} to grade
                    </span>
                  )}
                </div>

                <div className="mt-5 grid grid-cols-3 gap-2 border-t border-canvas-200 pt-4 text-xs">
                  <div>
                    <p className="text-brand-900/45 flex items-center gap-1">
                      <FontAwesomeIcon icon={faLayerGroup} className="h-3 w-3" /> Modules
                    </p>
                    <p className="font-semibold text-brand-950 mt-1">{course.moduleCount}</p>
                  </div>
                  <div>
                    <p className="text-brand-900/45 flex items-center gap-1">
                      <FontAwesomeIcon icon={faUserGraduate} className="h-3 w-3" /> Students
                    </p>
                    <p className="font-semibold text-brand-950 mt-1">{course.enrolledStudentCount}</p>
                  </div>
                  <div>
                    <p className="text-brand-900/45 flex items-center gap-1">
                      <FontAwesomeIcon icon={faClipboardList} className="h-3 w-3" /> To grade
                    </p>
                    <p className="font-semibold text-brand-950 mt-1">{course.pendingSubmissionsToGrade}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
